/**
 * SaveIndicator.tsx
 * AMÉLIORATION: Indicateur visuel de l'état de sauvegarde
 * Affiche l'état actuel (saving, saved, unsaved, error) avec animation
 * Utilise Lucide React pour les icônes et Tailwind pour les styles
 */
import React from 'react';
import { Loader2, Check, Circle, AlertTriangle, CircleDot, WifiOff } from 'lucide-react';
import { useAppStore } from '../../../services/StateService';

export function SaveIndicator(): React.ReactElement {
  const saveStatus = useAppStore(state => state.save.status);
  const lastSaved = useAppStore(state => state.save.lastSaved);
  const errorMessage = useAppStore(state => state.save.errorMessage);

  const getStatusInfo = () => {
    switch (saveStatus) {
      case 'saving':
        return {
          icon: Loader2,
          text: 'Sauvegarde...',
          colorClass: 'text-blue-500',
          bgClass: 'bg-blue-50',
          animate: true,
        };
      case 'saved':
        return {
          icon: Check,
          text: lastSaved ? `Sauvegardé ${formatTimeAgo(lastSaved)}` : 'Sauvegardé',
          colorClass: 'text-green-600',
          bgClass: 'bg-green-50',
          animate: false,
        };
      case 'unsaved':
        return {
          icon: CircleDot,
          text: 'Non sauvegardé',
          colorClass: 'text-amber-600',
          bgClass: 'bg-amber-50',
          animate: false,
        };
      case 'error':
        return {
          icon: AlertTriangle,
          text: errorMessage || 'Erreur de sauvegarde',
          colorClass: 'text-red-600',
          bgClass: 'bg-red-50',
          animate: false,
        };
      case 'conflict':
        return {
          icon: AlertTriangle,
          text: 'Conflit — synchro suspendue',
          colorClass: 'text-amber-700',
          bgClass: 'bg-amber-100',
          animate: false,
        };
      // A16 : état hors ligne — localStorage sauvegardé, cloud en attente.
      case 'offline':
        return {
          icon: WifiOff,
          text: 'Hors ligne — non synchronisé',
          colorClass: 'text-orange-600',
          bgClass: 'bg-orange-50',
          animate: false,
        };
      default:
        return {
          icon: Circle,
          text: 'Prêt',
          colorClass: 'text-gray-500',
          bgClass: 'bg-gray-50',
          animate: false,
        };
    }
  };

  const statusInfo = getStatusInfo();
  const Icon = statusInfo.icon;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 cursor-default ${statusInfo.colorClass} ${statusInfo.bgClass}`}
      title={statusInfo.text}
    >
      <Icon className={`w-3.5 h-3.5 ${statusInfo.animate ? 'animate-spin' : ''}`} />
      <span>{statusInfo.text}</span>
    </div>
  );
}

/**
 * Formate une date en "il y a X secondes/minutes"
 */
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);

  if (seconds < 10) return "à l'instant";
  if (seconds < 60) return `il y a ${seconds}s`;
  if (minutes < 60) return `il y a ${minutes}min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;

  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}
